import React, { useState, useEffect, useCallback } from 'react';
import { Guid } from '@Shared.Angular/@types/guid';
import { DeleteEntityTypes } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { SelectUser } from '@Shared.Angular/flowingly.components/SelectUser/SelectUser';
import { KendoReactComboBox } from '@Shared.Angular/kendo.react/KendoReactComboBox';
import { ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import IGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/group/groupDetail';
import './RunnerSetupDeleteSearch.scss';

interface IActorSelection {
  actor?: Guid;
}

type Props = {
  entityToDelete: IGroupDetail;
  entityName: string;
  teams: IGroupDetail[];
  onActorChosen: (actors: IActorSelection) => void;
};

export const RunnerSetupDeleteSearchTeam = ({
  entityToDelete,
  entityName,
  teams,
  onActorChosen
}: Props) => {
  const [actorType, setActorType] = useState<DeleteEntityTypes>(
    teams.length > 0
      ? DeleteEntityTypes.teamEntityType
      : DeleteEntityTypes.userEntityType
  );
  const [selectedActorId, setSelectedActorId] = useState<Guid | undefined>(
    undefined
  );
  const [selectedTeam, setSelectedTeam] = useState<IGroupDetail | null>(null);

  const raiseActorChange = useCallback(() => {
    onActorChosen({ actor: selectedActorId });
  }, [selectedActorId, onActorChosen]);

  useEffect(() => {
    raiseActorChange();
  }, [raiseActorChange]);

  const handleRadioChange = (newActorType: DeleteEntityTypes) => {
    setActorType(newActorType);
    setSelectedActorId(undefined);
    setSelectedTeam(null);
  };

  const handleTeamChange = (event: ComboBoxChangeEvent) => {
    const team = event.value as IGroupDetail;
    setSelectedTeam(team);
    if (team) {
      setSelectedActorId(team.id);
    }
  };

  return (
    <div className="delete-search">
      <div className="search-box">
        {/* REASSIGN ACTOR SECTION */}
        <div className="search-row">
          <div className="search-column">
            <label className="row-title">Reassign Actor</label>
            <p>
              To ensure in-progress & published flows continue to work, search
              for a User or Team, to replace <b>{entityName}</b>
            </p>
          </div>
          <div className="search-column">
            <ul className="actor-list">
              <li>
                <input
                  type="radio"
                  name="actorType"
                  id="radiouser"
                  checked={actorType === DeleteEntityTypes.userEntityType}
                  value={DeleteEntityTypes.userEntityType}
                  onChange={() => {
                    handleRadioChange(DeleteEntityTypes.userEntityType);
                  }}
                />
                <label htmlFor="radiouser">User</label>
              </li>
              {teams.length > 0 && (
                <li>
                  <input
                    type="radio"
                    name="actorType"
                    id="radiogroup"
                    checked={actorType === DeleteEntityTypes.teamEntityType}
                    value={DeleteEntityTypes.teamEntityType}
                    onChange={() => {
                      handleRadioChange(DeleteEntityTypes.teamEntityType);
                    }}
                  />
                  <label htmlFor="radiogroup">Team</label>
                </li>
              )}
            </ul>
            <div>
              {actorType === DeleteEntityTypes.userEntityType && (
                <SelectUser
                  selectedUserId={selectedActorId}
                  onUserSelect={setSelectedActorId}
                  containerClass="width-100"
                  exclude={entityToDelete.id}
                  showEmail={true}
                  inputId="reassign-actor-user-select"
                />
              )}
              {actorType === DeleteEntityTypes.teamEntityType && (
                <KendoReactComboBox
                  data={teams}
                  textField="name"
                  dataItemKey="id"
                  placeholder="Select Team"
                  value={selectedTeam}
                  onChange={handleTeamChange}
                />
              )}
            </div>
          </div>
        </div>
        {/* END REASSIGN ACTOR SECTION */}
      </div>
    </div>
  );
};
